// One plan being read, alone or together. The day's readings, each opening its passage page; marking
// the day read (a rubric hairline draws across the day, iOS FS-307); who has read it (presence, never
// ranking); the day's conversation (YouVersion's Plans with Friends); a month calendar whose read days
// carry the same hairline; pause and guilt-free catch-up; invites; and, at the end, a colophon. The
// reader's own days read are a dial (FS-375), never shown at zero. Calendar plans read by date show
// the date's own readings ("9 October"); others count days.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Avatar } from "../../../components/Avatar";
import { AvatarStack } from "../../../components/AvatarStack";
import { Button, IconButton, LinkButton } from "../../../components/Button";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { Sheet } from "../../../components/Dialog";
import { Dial } from "../../../components/Dial";
import { EmptyState } from "../../../components/Empty";
import { MentionTextarea } from "../../../components/MentionTextarea";
import { Menu } from "../../../components/Menu";
import { PlanCover } from "../../../components/PlanCover";
import { ReportDialog } from "../../../components/ReportDialog";
import { RichText } from "../../../components/RichText";
import { Skeleton, SkeletonText } from "../../../components/Skeleton";
import { Plate, Surface } from "../../../components/Surface";
import ui from "../../../components/ui.module.css";
import { TickBar, WeekStrip } from "../../../components/WeekStrip";
import { useAddDayNote, useDayNotes, useDeleteDayNote, useJourney, useJourneyAction, useLeavePlan } from "../../../data/planQueries";
import { atNoon, dateOfDay, dayOn, localDate, othersIn, progress, promptFor, type DayNote, type Journey, type Reading } from "../../../data/plans";
import { useCreatePost, useFriends } from "../../../data/queries";
import { shortTime } from "../../../lib/time";
import { useTitle } from "../../../lib/useTitle";
import { CompanionLine, ReadingRows } from "./-readings";
import s from "./journey.module.css";

/** Reflect on the day's first reading: a passage, or a book's section. */
const reflectOn = (r: Reading | undefined) => (!r ? {} : r.kind === "section" ? { book: r.libraryId, section: r.sectionUid } : { ref: r.label });

export const Route = createFileRoute("/_app/plans/$journeyId")({ component: JourneyPage });

const long = (iso: string) => atNoon(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
const first = (name: string) => name.split(" ")[0]!;
const names = (xs: string[]) => (xs.length < 3 ? xs.join(" and ") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

function JourneyPage() {
  const { journeyId } = Route.useParams();
  const { data: j, isPending } = useJourney(journeyId);
  useTitle(j?.plan.title ?? "Plan");
  if (isPending || (j && !j.plan.schedule))
    return (
      <div className={s.page} aria-busy="true">
        <div className={s.head}>
          <Skeleton width={72} height={115} shape="block" />
          <div className={s.headText}>
            <Skeleton width="60%" height={36} />
            <Skeleton width="40%" height={14} />
          </div>
        </div>
        <SkeletonText lines={4} />
      </div>
    );
  if (!j)
    return (
      <div className={s.page}>
        <EmptyState title="This plan isn’t available." body="You may have left it, or it was never yours to see.">
          <LinkButton to="/plans" variant="secondary">
            Go to Plans
          </LinkButton>
        </EmptyState>
      </div>
    );
  return <Reading key={j.id} j={j} />;
}

function Reading({ j }: { j: Journey }) {
  const act = useJourneyAction(j.id);
  const leave = useLeavePlan();
  const navigate = useNavigate();
  const today = localDate();
  const p = progress(j, today);
  const [day, setDay] = useState(p.today);
  const [inviting, setInviting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const others = othersIn(j);
  const schedule = j.plan.schedule!;
  const plan = schedule[day - 1]!;
  const isRead = j.me.read.includes(day);
  const date = dateOfDay(j, day, today);
  const at = p.position(day);
  const prev = at > 0 ? p.order[at - 1] : undefined;
  const next = at < p.order.length - 1 ? p.order[at + 1] : undefined;
  const byDate = j.follow === "byDate";
  const invite = `${location.origin}${j.invitePath}`;
  const daysRead = new Set(j.me.read).size;

  const items = [
    { label: "Invite friends", icon: "user-plus" as const, onSelect: () => setInviting(true) },
    {
      label: copied ? "Invite link copied" : "Copy invite link",
      icon: "link" as const,
      onSelect: () => {
        void navigator.clipboard?.writeText(invite);
        setCopied(true);
      },
    },
    { label: j.me.paused ? "Resume" : "Pause", icon: (j.me.paused ? "play" : "pause") as "play" | "pause", onSelect: () => act.mutate({ kind: "pause", paused: !j.me.paused }) },
    { label: "Leave plan", icon: "sign-out" as const, destructive: true, onSelect: () => setLeaving(true) },
  ];

  return (
    <div className={s.page}>
      <div className={ui.pageHead}>
        <LinkButton to="/plans" variant="ghost" size="sm" icon="back" className={s.backLink}>
          Plans
        </LinkButton>
        <span className={s.grow} />
        <Menu label="Plan actions" items={items} />
      </div>

      <header className={s.head}>
        <PlanCover plan={j.plan} width={72} />
        <div className={s.headText}>
          <h1 className={s.title}>{j.plan.title}</h1>
          <p className={s.meta}>
            {others.length > 0 && <AvatarStack users={others.map((m) => m.user)} max={4} size={22} />}
            <span>
              {j.together
                ? others.length
                  ? `Reading with ${names(others.map((m) => first(m.user.displayName)))}`
                  : "Reading together, once someone joins"
                : "Reading alone"}
              {j.invited.length > 0 && `. Invited: ${names(j.invited.map((u) => first(u.displayName)))}`}
              {byDate && ". By date"}
            </span>
          </p>
        </div>
        {daysRead > 0 && !p.finished && (
          <div className={s.dial}>
            <Dial value={daysRead} max={j.plan.days} label="Days read" size={76} />
          </div>
        )}
      </header>

      {/* This week at a glance (FW-006): each day a button that shows it, and the plan's days as ticks. */}
      {p.started && !p.finished && (
        <div className={s.week}>
          <WeekStrip journey={j} selected={day} onPick={setDay} />
          <TickBar read={daysRead} total={j.plan.days} />
        </div>
      )}

      {p.finished ? (
        <Colophon j={j} last={p.order.at(-1)!} />
      ) : j.me.paused ? (
        <Surface variant="panel" pad="sm" className={s.calm}>
          <p>Paused. Your place is kept; resume whenever you’re ready.</p>
          <Button size="sm" variant="secondary" icon="play" disabled={act.isPending} onClick={() => act.mutate({ kind: "pause", paused: false })}>
            Resume
          </Button>
        </Surface>
      ) : !p.started ? (
        <p className={s.calmLine}>This plan begins on {long(j.startedOn)}. You can look ahead at its days.</p>
      ) : (
        p.behind && (
          <Surface variant="panel" pad="md" className={s.catchUp} role="status">
            <p className={s.catchUpWords}>You’re a few readings back. That’s all right. Where would you like to pick up?</p>
            <div className={s.row}>
              <Button
                variant="secondary"
                size="sm"
                disabled={act.isPending}
                onClick={() => act.mutate({ kind: "catch-up", how: "start-from-today" }, { onSuccess: (x) => x && setDay(progress(x).today) })}
              >
                Start from today
              </Button>
              <Button variant="text" size="sm" disabled={act.isPending} onClick={() => act.mutate({ kind: "catch-up", how: "mark-read" })}>
                Mark earlier readings read
              </Button>
            </div>
          </Surface>
        )
      )}

      <section className={s.day} aria-labelledby="day-head">
        <div className={s.dayNav}>
          <IconButton label="Previous day" icon="back" look="outline" disabled={prev === undefined} onClick={() => prev && setDay(prev)} />
          <div className={s.dayLabel} key={day}>
            <h2 id="day-head" className={s.dayTitle}>
              {byDate ? (
                plan.heading
              ) : (
                <>
                  Day {day}
                  <span className={s.dayOf}> of {j.plan.days}</span>
                </>
              )}
            </h2>
            <p className={s.dayDate}>
              {j.plan.scheduleKind === "calendar" && !byDate ? `${long(date)}, the readings for ${plan.heading}` : long(date)}
            </p>
            {day !== p.today && (
              <Button variant="text" size="sm" onClick={() => setDay(p.today)}>
                Return to today
              </Button>
            )}
          </div>
          <IconButton label="Next day" icon="forward" look="outline" disabled={next === undefined} onClick={() => next && setDay(next)} />
        </div>
        {/* The day's mark: a rubric hairline drawn left to right once it's read (FS-307). */}
        <span className={s.hairline} data-read={isRead || undefined} aria-hidden="true" />

        <ReadingRows key={day} readings={plan.readings} journeyId={j.id} day={day} />
        {j.plan.companionWork ? (
          <CompanionLine work={j.plan.companionWork} className={s.companion} />
        ) : (
          j.plan.companion && (
            <p className={s.companion}>
              Beside it: <cite>{j.plan.companion}</cite>
            </p>
          )
        )}

        <div className={s.markRow}>
          <Button
            variant={isRead ? "secondary" : "primary"}
            icon={isRead ? "check" : undefined}
            className={s.mark}
            aria-pressed={isRead}
            onClick={() => act.mutate({ kind: "read", day, read: !isRead })}
          >
            {isRead ? "Read" : "Mark as read"}
          </Button>
          {j.together && <Presence j={j} day={day} />}
        </div>
      </section>

      {j.together ? (
        <Discussion j={j} day={day} />
      ) : (
        <section className={s.alone}>
          <p className={s.prompt}>{promptFor(j.plan, day)}</p>
          <LinkButton to="/reflect" search={reflectOn(plan.readings[0])} variant="ghost" size="sm" icon="note">
            Reflect on it, privately
          </LinkButton>
        </section>
      )}

      <Calendar j={j} selected={day} today={today} onSelect={setDay} />

      <InviteSheet j={j} invite={invite} open={inviting} onClose={() => setInviting(false)} />
      {leaving && (
        <ConfirmDialog
          title="Leave this plan?"
          body={j.together ? "You’ll stop seeing the group’s days and conversation. What you wrote stays for them." : "Your progress in this plan will be cleared."}
          confirm="Leave plan"
          onConfirm={() => leave.mutate(j.id, { onSuccess: () => void navigate({ to: "/plans" }) })}
          onClose={() => setLeaving(false)}
        />
      )}
    </div>
  );
}

/** Who has read this day. Faces of those who have; a plain line for the rest. No counts, no order by progress. */
function Presence({ j, day }: { j: Journey; day: number }) {
  const others = othersIn(j);
  const read = others.filter((m) => m.read.includes(day));
  if (others.length === 0) return <p className={s.presenceText}>No one else has joined yet.</p>;
  return (
    <p className={s.presence}>
      {read.length > 0 && <AvatarStack users={read.map((m) => m.user)} max={5} size={26} />}
      <span className={s.presenceText}>
        {read.length === 0
          ? "No one has read this day yet."
          : `${names(read.map((m) => first(m.user.displayName)))} ${read.length === 1 ? "has" : "have"} read it${read.length === others.length ? " too" : ""}.`}
      </span>
    </p>
  );
}

function Discussion({ j, day }: { j: Journey; day: number }) {
  const { data } = useDayNotes(j.id, day);
  const add = useAddDayNote(j.id, day);
  const [text, setText] = useState("");
  const notes = data?.items ?? [];
  const label = j.follow === "byDate" ? j.plan.schedule![day - 1]!.heading : `day ${day}`;
  return (
    <section className={s.discussion} aria-labelledby="talk-head">
      <h2 id="talk-head" className={s.prompt}>
        {data?.prompt ?? promptFor(j.plan, day)}
      </h2>
      {data && notes.length === 0 ? (
        <p className={s.quiet}>No one has said anything about {label} yet.</p>
      ) : (
        <ul className={s.notes}>
          {notes.map((n) => (
            <Note key={n.id} n={n} />
          ))}
        </ul>
      )}
      <form
        className={s.noteForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) add.mutate(text, { onSuccess: () => setText("") });
        }}
      >
        <label htmlFor="day-note" className="visually-hidden">
          Add to the conversation about {label}
        </label>
        <MentionTextarea id="day-note" className={ui.area} rows={2} maxLength={1000} placeholder={`Say something about ${label}`} value={text} onValueChange={setText} />
        {add.isError && (
          <p className={ui.error} role="alert">
            {add.error.message}
          </p>
        )}
        <Button type="submit" variant="primary" size="sm" disabled={!text.trim() || add.isPending}>
          Post
        </Button>
      </form>
    </section>
  );
}

function Note({ n }: { n: DayNote }) {
  const del = useDeleteDayNote(n.journeyId, n.day);
  const [reporting, setReporting] = useState(false);
  return (
    <li>
      <Avatar user={n.author} size={32} />
      <div>
        <p className={s.noteBy}>
          {n.author.displayName} <span className={s.muted}>{shortTime(n.createdAt)}</span>
          {n.hidden && <span className={s.muted}> · hidden by moderators, seen only by you</span>}
        </p>
        <p className={s.noteText}>
          <RichText text={n.body} />
        </p>
        <p className={s.noteActions}>
          {n.canDelete ? (
            <button type="button" className={ui.text} disabled={del.isPending} onClick={() => del.mutate(n.id)} aria-label={`Delete your note from ${shortTime(n.createdAt)}`}>
              Delete
            </button>
          ) : (
            <button type="button" className={ui.text} onClick={() => setReporting(true)} aria-label={`Report note by ${n.author.displayName}`}>
              Report
            </button>
          )}
        </p>
      </div>
      {reporting && <ReportDialog target={{ kind: "journey_note", id: n.id }} name={n.author.displayName} onClose={() => setReporting(false)} />}
    </li>
  );
}

function Calendar({ j, selected, today, onSelect }: { j: Journey; selected: number; today: string; onSelect: (d: number) => void }) {
  const sel = atNoon(dateOfDay(j, selected, today));
  const y = sel.getUTCFullYear();
  const mo = sel.getUTCMonth();
  const days = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  const lead = (new Date(Date.UTC(y, mo, 1)).getUTCDay() + 6) % 7;
  const monthName = sel.toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" });
  const todayDay = dayOn(j, today);
  return (
    <section className={s.calendar} aria-labelledby="cal-head">
      <h2 id="cal-head" className={s.month}>
        {sel.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })}
      </h2>
      <div className={s.grid}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <span key={i} className={s.dow} aria-hidden="true">
            {d}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`l${i}`} />
        ))}
        {Array.from({ length: days }, (_, i) => i + 1).map((n) => {
          const iso = `${y}-${String(mo + 1).padStart(2, "0")}-${String(n).padStart(2, "0")}`;
          // The plan day read on this date, if one is (not a resting Sunday, not outside the plan).
          const d = dayOn(j, iso);
          const planDay = d !== null && dateOfDay(j, d, today) === iso ? d : null;
          if (planDay === null) {
            const sunday = new Date(Date.UTC(y, mo, n)).getUTCDay() === 0;
            return (
              <span key={n} className={s.off} title={sunday && j.plan.restSundays ? "The Lord’s Day: no reading set" : undefined}>
                {n}
              </span>
            );
          }
          const read = j.me.read.includes(planDay);
          const isToday = iso === today && planDay === todayDay;
          return (
            <button
              key={n}
              type="button"
              disabled={iso > today}
              aria-pressed={planDay === selected}
              aria-label={`${n} ${monthName}, ${j.follow === "byDate" ? j.plan.schedule![planDay - 1]!.heading : `day ${planDay}`}${read ? ", read" : ""}${isToday ? ", today" : ""}`}
              className={[s.cell, read ? s.read : "", planDay === selected ? s.sel : "", isToday ? s.today : ""].join(" ")}
              onClick={() => onSelect(planDay)}
            >
              {n}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** The plan's end, set like a book's colophon on a plate. Shareable as a milestone, by choice. */
function Colophon({ j, last }: { j: Journey; last: number }) {
  const create = useCreatePost();
  const navigate = useNavigate();
  const others = othersIn(j).map((m) => first(m.user.displayName));
  const end = atNoon(dateOfDay(j, last));
  const span = `${atNoon(j.startedOn).toLocaleDateString("en-GB", { day: "numeric", month: "long" })} to ${end.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`;
  const line = `Finished ${j.plan.title}${others.length ? ` with ${names(others)}` : ""}`;
  return (
    <Plate as="section" pad="lg" className={s.colophon} aria-label="Plan finished">
      <span className={s.ornament} aria-hidden="true">
        <i />
      </span>
      <p className={s.colophonTitle}>{j.plan.title}</p>
      <p className={s.colophonLine}>
        read {others.length ? `together with ${names(others)}` : "through"}, {j.plan.days} days
      </p>
      <p className={s.colophonDate}>{span}</p>
      <Button
        variant="secondary"
        icon="share"
        busy={create.isPending}
        onClick={() => create.mutate({ kind: "milestone", title: line, body: "", visibility: "friends" }, { onSuccess: (p) => void navigate({ to: "/p/$postId", params: { postId: p.id } }) })}
      >
        Share with friends
      </Button>
    </Plate>
  );
}

function InviteSheet({ j, invite, open, onClose }: { j: Journey; invite: string; open: boolean; onClose: () => void }) {
  const { data: friends } = useFriends();
  const act = useJourneyAction(j.id);
  const [chosen, setChosen] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const available = (friends?.friends ?? []).filter((f) => !j.members.some((m) => m.user.id === f.id) && !j.invited.some((i) => i.id === f.id));
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Invite to ${j.plan.title}`}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
          <Button
            variant="primary"
            disabled={!chosen.length || act.isPending}
            onClick={() =>
              act.mutate(
                { kind: "invite", userIds: chosen },
                {
                  onSuccess: () => {
                    setChosen([]);
                    onClose();
                  },
                },
              )
            }
          >
            Invite
          </Button>
        </>
      }
    >
      <div className={s.sheetBody}>
        {available.length === 0 ? (
          <p className={s.small}>All your friends are here or invited. Share the invite link with anyone else.</p>
        ) : (
          <fieldset className={s.friends}>
            <legend className="visually-hidden">Friends</legend>
            {available.map((f) => (
              <label key={f.id} className={s.friend}>
                <Avatar user={f} size={32} />
                <span className={s.grow}>{f.displayName}</span>
                <input type="checkbox" checked={chosen.includes(f.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, f.id] : chosen.filter((x) => x !== f.id))} />
              </label>
            ))}
          </fieldset>
        )}
        {j.invited.length > 0 && (
          <div className={s.friends}>
            <p className={s.legend}>Invited, not yet joined</p>
            {j.invited.map((u) => (
              <div key={u.id} className={s.friend}>
                <Avatar user={u} size={32} />
                <span className={s.grow}>{u.displayName}</span>
                <Button variant="text" size="sm" onClick={() => act.mutate({ kind: "withdraw", userId: u.id })}>
                  Withdraw
                </Button>
              </div>
            ))}
          </div>
        )}
        <Surface variant="panel" pad="sm" className={s.linkRow}>
          <p className={s.small}>Anyone with the link can join once they’re signed in, friends or not yet.</p>
          <Button
            variant="secondary"
            size="sm"
            icon="link"
            onClick={() => {
              void navigator.clipboard?.writeText(invite);
              setCopied(true);
            }}
          >
            {copied ? "Link copied" : "Copy invite link"}
          </Button>
        </Surface>
      </div>
    </Sheet>
  );
}

